/**
 * Validation Status Component for dev-tools
 * Part of Phase 3 refactoring - Component Decomposition (UI Components)
 *
 * Shows validation status and allows toggling validation
 */
import React from "react";

import { CheckCircle, Error, Warning } from "@mui/icons-material";
import { Box, Chip, FormControlLabel, Switch, Typography } from "@mui/material";

interface ValidationStatusProps {
  validationEnabled: boolean;
  errorCount: number;
  onToggle: () => void;
  isValid?: boolean;
}

export const ValidationStatus: React.FC<ValidationStatusProps> = ({
  validationEnabled,
  errorCount,
  onToggle,
}) => {
  const getStatusIcon = () => {
    if (!validationEnabled) {
      return <Warning color="warning" fontSize="small" />;
    }

    if (errorCount > 0) {
      return <Error color="error" fontSize="small" />;
    }

    return <CheckCircle color="success" fontSize="small" />;
  };

  const getStatusText = () => {
    if (!validationEnabled) {
      return "Validation disabled";
    }

    if (errorCount > 0) {
      return `${errorCount} validation error${errorCount > 1 ? "s" : ""}`;
    }

    return "All fields valid";
  };

  const getStatusColor = ():
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" => {
    if (!validationEnabled) return "warning";
    if (errorCount > 0) return "error";
    return "success";
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
      <FormControlLabel
        control={
          <Switch
            checked={validationEnabled}
            onChange={onToggle}
            color="primary"
          />
        }
        label="Enable Validation"
      />

      <Chip
        icon={getStatusIcon()}
        label={getStatusText()}
        color={getStatusColor()}
        variant="outlined"
        size="small"
      />

      {validationEnabled && errorCount === 0 && (
        <Typography variant="caption" color="text.secondary">
          Form is ready to submit
        </Typography>
      )}
    </Box>
  );
};
